import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Tooltip } from 'antd';
import { CloudServerOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { env } from '@/lib/env';
import { HEALTH_STATUS_META } from '@/features/system/healthStatus';

// The card at the top of the RIGHT rail: which environment you are operating,
// and whether its dependencies answer. The dot is the live System Health roll-up
// (SystemHealthStore, polled every 60 s while the app is open, 30 s while the
// System Health page is) — grey when unknown, never a guessed green.

const RAIL_POLL_MS = 60_000;

const ENV_LABEL: Record<typeof env.appEnv, string> = {
  production: 'Production',
  qa: 'QA',
  dev: 'Development',
};

export const PlatformRailCard = observer(function PlatformRailCard({ wide, isDark }: { wide: boolean; isDark: boolean }) {
  const { systemHealthStore: health } = useStore();
  useEffect(() => health.subscribe(RAIL_POLL_MS), [health]);

  const meta = HEALTH_STATUS_META[health.status];
  const envLabel = ENV_LABEL[env.appEnv] ?? env.appEnv;
  const statusLine = health.error ? 'Health check unreachable' : health.checkedAt == null ? 'Checking health…' : `System ${meta.label.toLowerCase()}`;

  const tile = (
    <span
      style={{
        width: 34,
        height: 34,
        borderRadius: 10,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        color: '#FFFFFF',
        fontSize: 16,
        background: `linear-gradient(135deg, ${colors.violet[500]}, ${colors.violet[700]})`,
        position: 'relative',
      }}
    >
      <CloudServerOutlined />
      {!wide && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            right: -2,
            top: -2,
            width: 9,
            height: 9,
            borderRadius: 999,
            background: meta.color,
            boxShadow: `0 0 0 2px ${isDark ? colors.slate[900] : '#FBFAFF'}`,
          }}
        />
      )}
    </span>
  );

  return (
    <Tooltip title={wide ? 'Open System health' : `${envLabel} · ${statusLine}`} placement="right" mouseEnterDelay={0.3}>
      <Link
        to="/system"
        aria-label={`${envLabel} platform. ${statusLine}. Open System health`}
        style={{
          margin: wide ? '12px 10px 6px' : '12px 8px 6px',
          padding: wide ? '10px 10px' : 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: wide ? 'flex-start' : 'center',
          gap: 10,
          borderRadius: 12,
          border: `1px solid ${isDark ? colors.slate[700] : colors.violet[200]}`,
          background: isDark ? colors.slate[800] : `linear-gradient(180deg, ${colors.violet[50]}, #FFFFFF)`,
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        {tile}
        {wide && (
          <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: isDark ? colors.slate[400] : colors.slate[500] }}>
              Platform
            </span>
            <span style={{ fontSize: 13.5, fontWeight: 650, color: isDark ? colors.slate[50] : colors.slate[900] }}>{envLabel}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: isDark ? colors.slate[400] : colors.slate[500], whiteSpace: 'nowrap' }}>
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: 999, background: meta.color }} />
              {statusLine}
            </span>
          </span>
        )}
      </Link>
    </Tooltip>
  );
});
